// Address clean-up and checks, shared by the importer (browser) and the API (Worker).

const TYPO_DOMAINS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.om': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'outlok.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'iclod.com': 'icloud.com',
  'icloud.con': 'icloud.com',
  'icoud.com': 'icloud.com',
  'aol.con': 'aol.com',
  'comcast.ent': 'comcast.net',
  'sbcglobal.ent': 'sbcglobal.net',
};

export function normalizeEmail(raw: string): string {
  return (raw || '')
    .trim()
    .replace(/^mailto:/i, '')
    .replace(/^.*<([^>]+)>.*$/, '$1')
    .replace(/[\s​ ]+/g, '')
    .replace(/[.,;]+$/, '')
    .toLowerCase();
}

/**
 * The address in a spreadsheet cell: "Ann <ann@x.com>", "ann @x.com", or the first of several addresses.
 * If nothing in the cell is valid, returns the first word cleaned up, so it can be shown as skipped.
 */
export function firstEmail(cell: string): string {
  const s = cell || '';
  const angle = /<([^>]+)>/.exec(s);
  if (angle && isValidEmail(normalizeEmail(angle[1]))) return normalizeEmail(angle[1]);
  const words = s.split(/[\s,;]+/).filter(Boolean);
  for (const w of words) {
    const e = normalizeEmail(w);
    if (isValidEmail(e)) return e;
  }
  // No single word is an address. Maybe stray spaces split one: "ann @example.com".
  const whole = normalizeEmail(s);
  if (isValidEmail(whole)) return whole;
  return normalizeEmail(words[0] || '');
}

/** Practical check: one @, a dotted domain with a 2+ letter ending, no spaces or double dots. */
export function isValidEmail(e: string): boolean {
  if (e.length > 254 || e.length < 6) return false;
  const m = /^([a-z0-9!#$%&'*+/=?^_`{|}~.-]+)@([a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,24})$/.exec(e);
  if (!m) return false;
  const [, local, domain] = m;
  if (local.length > 64 || local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
  if (domain.split('.').some((p) => !p || p.startsWith('-') || p.endsWith('-'))) return false;
  return true;
}

export function emailDomain(e: string): string {
  return e.slice(e.lastIndexOf('@') + 1);
}

/** If the domain is a known typo, the fixed address; else null. */
export function suggestFix(e: string): string | null {
  const d = emailDomain(e);
  const fix = TYPO_DOMAINS[d];
  return fix ? e.slice(0, e.lastIndexOf('@') + 1) + fix : null;
}
